import { useMemo, useState } from 'react'
import { CardGrid } from '../components/card/CardGrid'
import { ContentWidth } from '../components/layout/ContentWidth'
import { Card } from '../components/card/Card'
import { DataTable } from '../components/table/DataTable'
import type { Column, SortState } from '../components/table/types'
import { DetailSection } from '../components/detail/DetailSection'
import { Field, FieldDivider } from '../components/detail/FieldGrid'
import { Button } from '../components/common/Button'
import { Chip } from '../components/common/Chip'
import type { ChipTone } from '../components/common/Chip'
import { Input, Select, Toggle } from '../components/common/Controls'
import { MultiSelect } from '../components/common/MultiSelect'
import type { MultiSelectOption } from '../components/common/MultiSelect'
import { EmptyState, ErrorBox, Loading, Note, WarnBox } from '../components/common/Feedback'
import { FilterBar, FilterCount, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { HelpIcon } from '../components/common/HelpIcon'
import { RefPopover } from '../components/common/RefPopover'
import type { RefDetail } from '../components/common/RefPopover'
import { Tabs } from '../components/common/Tabs'
import { Tree } from '../components/common/Tree'
import type { TreeNode } from '../components/common/Tree'
import s from './ComponentsPage.module.scss'

/**
 * Die Abnahmeseite.
 *
 * Zeigt jede Komponente in ihren Zuständen. Zweck: Das Grundgerüst ist
 * prüfbar, bevor es eine Fachseite gibt — und bleibt prüfbar, wenn später
 * jemand eine Komponente anfasst.
 *
 * Die Beispieldaten sind ohne Fachbedeutung. Sie zeigen STRUKTUREN — kurze und
 * lange Bezeichnungen, Kennungen, Beträge, Fehlwerte —, weil daran hängt, ob
 * eine Spalte hält. Was ein Wert bedeutet, ist Sache der Fachseiten.
 */

// -------------------------------------------------------------- Beispieldaten

interface DemoRow {
  id: number
  name: string
  code: string
  amount: number
  count: number
  share: number
  date: string
  state: 'gut' | 'mittel' | 'schlecht'
  note: string | null
}

const STATES: DemoRow['state'][] = ['gut', 'mittel', 'schlecht']

const ROWS: DemoRow[] = Array.from({ length: 47 }, (_, index) => ({
  id: index + 1,
  // Absichtlich unterschiedlich lang: Eine Spalte, die nur mit gleich langen
  // Werten getestet wird, bricht beim ersten echten Datensatz.
  name: index % 7 === 0
    ? `Sehr lange Bezeichnung Nummer ${index + 1} mit Zusatz`
    : `Eintrag ${index + 1}`,
  code: `XX${String(index + 1).padStart(10, '0')}`,
  amount: 1000 - index * 7.35,
  count: (index * 137) % 9000,
  share: ((index * 13) % 100) / 100,
  date: `2026-0${(index % 9) + 1}-1${index % 9}`,
  state: STATES[index % 3],
  // Jeder dritte ohne Wert: Fehlwerte sind der Normalfall, nicht die Ausnahme.
  note: index % 3 === 0 ? null : `Bemerkung zu ${index + 1}`,
}))

const PAGE_SIZE = 10

/** Womit die Demo der festen Hoehe aufmacht. */
const SICHTBAR_START = 10

// ------------------------------------------------------------------- Formate

const money = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2,
                                               maximumFractionDigits: 2 })
const percent = new Intl.NumberFormat('de-DE', { style: 'percent',
                                                 maximumFractionDigits: 1 })

/** Der Ton zum Zustand. Steht einmal hier, damit Tabelle und Detail gleich sind. */
function tone(state: DemoRow['state']): ChipTone {
  if (state === 'gut') return 'good'
  return state === 'mittel' ? 'warn' : 'bad'
}

//: Echte Handelsplätze als Demodaten, weil der Fall daran haengt: Kuerzel
//: und Kennung weichen voneinander ab (NASDAQ gegen XNAS), und drei Boersen
//: tragen dasselbe Kuerzel BSE. Erfundene Werte wuerden genau das verdecken,
//: was die Komponente loesen soll.
const PLAETZE: MultiSelectOption[] = [
  { value: 'XNAS', label: 'NASDAQ', hint: 'Nasdaq' },
  { value: 'XNYS', label: 'NYSE', hint: 'New York Stock Exchange' },
  { value: 'ARCX', label: 'NYSE ARCA', hint: 'NYSE Arca' },
  { value: 'XETR', label: 'XETRA', hint: 'Xetra' },
  { value: 'XFRA', label: 'FSX', hint: 'Frankfurt Stock Exchange' },
  { value: 'XLON', label: 'LSE', hint: 'London Stock Exchange' },
  { value: 'XSWX', label: 'SIX', hint: 'SIX Swiss Exchange' },
  { value: 'XTKS', label: 'TSE', hint: 'Tokyo Stock Exchange' },
  { value: 'XHKG', label: 'HKEX', hint: 'Hong Kong Exchanges and Clearing' },
  { value: 'XBOM', label: 'BSE', hint: 'BSE Ltd, Mumbai' },
  { value: 'XBUD', label: 'BSE', hint: 'Budapest Stock Exchange' },
  { value: 'XBOT', label: 'BSE', hint: 'Botswana Stock Exchange' },
  { value: 'XNSE', label: 'NSE', hint: 'National Stock Exchange of India' },
  { value: 'XNAI', label: 'NSE', hint: 'Nairobi Securities Exchange' },
  { value: 'OTCM', label: 'OTC', hint: 'OTC Markets' },
]

//: Ein Baum MIT Ebenen, und zwar ein echter Ausschnitt aus GICS.
//:
//: Erfundene Beispieldaten waeren hier schlechter: Der Fall, um den es geht —
//: eine Zwischenebene, an der selbst Instrumente haengen — sieht man nur, wenn
//: die Zahlen zueinander passen. 2.911 = 1.884 + 712 + 315.
//:
//: Und ausdruecklich NICHT der heutige Bestand: Der GICS-Baum in der Datenbank
//: ist flach, weil nur die oberste Ebene frei verfuegbar ist. Wer die
//: Komponente hier vorgefuehrt bekommt, soll sehen, was sie kann.
const BAUM: TreeNode[] = [
  {
    id: '45', label: 'Information Technology', hint: 'GICS 45', value: '6.239',
    children: [
      {
        id: '4510', label: 'Software & Services', hint: 'GICS 4510',
        value: '2.911',
        children: [
          { id: '451030', label: 'Software', hint: 'GICS 451030', value: '1.884' },
          { id: '451020', label: 'IT Services', hint: 'GICS 451020', value: '712' },
          { id: '451010', label: 'Internet Services', hint: 'GICS 451010', value: '315' },
        ],
      },
      { id: '4530', label: 'Semiconductors & Equipment', hint: 'GICS 4530',
        value: '1.702',
        children: [
          { id: '453010', label: 'Semiconductors', hint: 'GICS 453010', value: '1.702' },
        ] },
      // Ein Knoten OHNE Kinder auf derselben Ebene wie einer mit: Daran sieht
      // man, dass die Beschriftungsspalte nicht springt.
      { id: '4520', label: 'Technology Hardware', hint: 'GICS 4520', value: '1.626' },
    ],
  },
  { id: '20', label: 'Industrials', hint: 'GICS 20', value: '9.578' },
  { id: '40', label: 'Financials', hint: 'GICS 40', value: '5.997' },
]

const REITER = [
  { key: 'eins', label: 'Erster' },
  { key: 'zwei', label: 'Zweiter', badge: 47 },
  { key: 'drei', label: 'Dritter' },
  { key: 'gesperrt', label: 'Gesperrt', disabled: true },
]


//: Die vier Erscheinungsformen des Verweis-Popovers.
//:
//: VIER UND NICHT EINE: Ein Instrument hat ISIN, Kurs und Stammdaten, ein
//: Land hat nur seinen Verweis, ein Rohstoff liegt dazwischen, und ein totes
//: Ziel hat nicht einmal einen Namen. Wer die Komponente anfasst, muss alle
//: vier sehen — sonst faellt erst im Betrieb auf, dass der Kopf ohne `art`
//: zusammenfaellt oder der Fuss ohne Aktionen einen leeren Rand zieht.
const REF_DEMOS: RefDetail[] = [
  {
    ref: 'isin:US4581401001',
    kind: 'isin',
    name: 'Intel Corporation',
    art: 'Instrument',
    schluessel: { feld: 'ISIN', wert: 'US4581401001' },
    daten: [{ feld: 'Ticker', wert: 'NASDAQ:INTC' },
            { feld: 'Börse', wert: 'NASDAQ' }],
    kurs: { betrag: '202,14 USD' },
    ampel: {
      stufe: 'GREEN',
      seit: 'seit 3 Tagen',
      satz: 'Auftragsbuch bleibt voll, Data Center trägt das Wachstum',
    },
    news: {
      anzahl: 3,
      zuletzt: 'vor 2 Std.',
      items: [
        { id: 1, sentiment: -2, headline: 'Chipmarkt unter Druck' },
        { id: 2, sentiment: 1, headline: 'Fertigung in Ohio startet früher' },
      ],
    },
    aktionen: [{ text: 'Stammdaten', to: '/instruments/1' },
               { text: 'News', to: '/news?isin=US4581401001' },
               { text: 'Ampel', to: '/assessments?ref=isin:US4581401001' }],
  },
  {
    ref: 'comm:BRENT',
    kind: 'comm',
    name: 'Rohöl Brent',
    art: 'Rohstoff · ICE',
    schluessel: { feld: 'Code', wert: 'BRENT' },
    daten: [{ feld: 'Einheit', wert: 'Barrel' }],
    kurs: { betrag: '92,06 USD' },
    news: {
      anzahl: 12,
      zuletzt: 'vor 40 Min.',
      items: [
        { id: 3, sentiment: -1, headline: 'Öl fällt weitere 3 Prozent' },
      ],
    },
    aktionen: [{ text: 'News', to: '/news?commodity=BRENT' }],
  },
  {
    ref: 'geo:ISO3166:DE',
    kind: 'geo',
    name: 'Deutschland',
    art: 'Land · ISO 3166',
    // OHNE KURS UND OHNE AMPEL: Fuer ein Land gibt es beides nicht. Der
    // Abschnitt faellt weg statt leer dazustehen.
    schluessel: { feld: 'Verweis', wert: 'geo:ISO3166:DE' },
    news: { anzahl: 5, zuletzt: 'vor 6 Std.', items: [] },
    aktionen: [{ text: 'Meldungen dazu', to: '/news' }],
  },
  {
    // DER FALL, DEN ES WIRKLICH GIBT: `refs` traegt keinen Fremdschluessel,
    // ein Ziel kann nach dem Schreiben verschwinden.
    ref: 'ind:IW:99999',
    kind: 'ind',
    name: null,
  },
]

const REF_TEXTE = ['Intel Corporation', 'Rohöl Brent', 'Deutschland',
                   '⚠ ind:IW:99999']

export function ComponentsPage() {
  const [sort, setSort] = useState<SortState>({ key: 'id', descending: false })
  const [page, setPage] = useState(1)
  const [sichtbar, setSichtbar] = useState<number | null>(SICHTBAR_START)
  const [search, setSearch] = useState('')
  const [group, setGroup] = useState('alle')
  const [onlyWithNote, setOnlyWithNote] = useState(false)
  const [selected, setSelected] = useState<DemoRow | null>(null)
  const [reiter, setReiter] = useState('eins')
  const [offen, setOffen] = useState<string[]>(['45', '4510'])
  const [gewaehlt, setGewaehlt] = useState<string>()
  const [plaetze, setPlaetze] = useState<string[]>([])

  // Sortieren und Blättern übernimmt hier die Seite. Die Tabelle tut es
  // bewusst nicht: Bei serverseitiger Paginierung sähe sie nur die aktuelle
  // Seite und würde 10 von 47 Zeilen sortieren.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return ROWS.filter((row) => {
      if (term && !row.name.toLowerCase().includes(term)
               && !row.code.toLowerCase().includes(term)) return false
      if (group !== 'alle' && row.state !== group) return false
      if (onlyWithNote && row.note == null) return false
      return true
    })
  }, [search, group, onlyWithNote])

  const sorted = useMemo(() => {
    const factor = sort.descending ? -1 : 1
    return [...filtered].sort((a, b) => {
      const left = a[sort.key as keyof DemoRow]
      const right = b[sort.key as keyof DemoRow]
      if (left == null) return 1
      if (right == null) return -1
      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * factor
      }
      return String(left).localeCompare(String(right), 'de-DE') * factor
    })
  }, [filtered, sort])

  const visible = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const columns: Column<DemoRow>[] = [
    { key: 'name', label: 'Bezeichnung', sortable: true, minWidth: '220px' },
    // Kopierknopf, Anordnung 1: Der Wert steht links, der Knopf am rechten
    // Zellenrand — immer an derselben Stelle, egal wie lang der Wert ist.
    // `minWidth` ist hier keine Kosmetik: Ohne sie ist die Spalte genau so
    // breit wie ihr Inhalt, und ein Knopf "am rechten Zellenrand" klebt am
    // Wert — es gibt keinen freien Platz zu verteilen.
    { key: 'code', label: 'Kennung', sortable: true, mono: true, copy: true,
      minWidth: '190px',
      help: 'Zwölfstellige Kennung. Steht in Monospace, damit Stellen untereinander liegen. Der Knopf rechts kopiert sie in die Zwischenablage.' },
    // Kopierknopf, Anordnung 2: Zahl und Knopf rechts beieinander, Knopf VOR
    // der Zahl. Kopiert wird der ROHE Wert (1000) und nicht die Darstellung
    // (1.000,00) — weitergeben will man die Zahl zum Weiterrechnen.
    { key: 'amount', label: 'Betrag', sortable: true, align: 'right', mono: true,
      render: (row) => money.format(row.amount),
      copy: (row) => row.amount.toFixed(2),
      help: 'Ein Betrag mit zwei Nachkommastellen, rechts ausgerichtet — sonst stehen Einer nicht unter Einern. Der Knopf kopiert 1000.00, nicht 1.000,00.' },
    { key: 'count', label: 'Anzahl', sortable: true, align: 'right', mono: true,
      render: (row) => row.count.toLocaleString('de-DE') },
    { key: 'share', label: 'Anteil', sortable: true, align: 'right', mono: true,
      render: (row) => percent.format(row.share) },
    { key: 'date', label: 'Datum', sortable: true, mono: true, hideOn: 'mobile',
      help: 'Auf dem Handy ausgeblendet — ein Beispiel für hideOn. Der Regelfall ist, dass Spalten bleiben und die Tabelle seitlich scrollt.' },
    { key: 'state', label: 'Zustand', sortable: true,
      render: (row) => <Chip tone={tone(row.state)}>{row.state}</Chip> },
    { key: 'note', label: 'Bemerkung', minWidth: '200px', wrap: true,
      hideOn: 'tablet' },
  ]

  return (
    <>
      <section className={s.block} data-demo="karten">
      <div className={s.heading}>Karten — Breiten im Raster</div>
      <CardGrid>
        <Card title="span 8" label span={8} meta="Desktop 8/12">
          <span className={s.spanMark}>
            Desktop 8 · Tablet 12 · Mobile 12
          </span>
        </Card>
        <Card title="span 4" label span={4} meta="Desktop 4/12">
          <span className={s.spanMark}>Desktop 4 · Tablet 6 · Mobile 12</span>
        </Card>
        <Card title="span 6" label span={6}>
          <span className={s.spanMark}>Desktop 6 · Tablet 12 · Mobile 12</span>
        </Card>
        <Card title="span 6 mit Fußzeile" label span={6}
              footer="Fußzeile einer gepolsterten Karte">
          <span className={s.spanMark}>Desktop 6 · Tablet 12 · Mobile 12</span>
        </Card>
        <Card title="span 3" label span={3}>
          <span className={s.spanMark}>Desktop 3 · Tablet 6</span>
        </Card>
        <Card title="span 3" label span={3}>
          <span className={s.spanMark}>Desktop 3 · Tablet 6</span>
        </Card>
        <Card title="span 3" label span={3}>
          <span className={s.spanMark}>Desktop 3 · Tablet 6</span>
        </Card>
        <Card title="span 3 mit Erklärung" label span={3}
              help="Das Fragezeichen sitzt auch am Kartentitel — dieselbe Komponente wie im Spaltenkopf.">
          <span className={s.spanMark}>Desktop 3 · Tablet 6</span>
        </Card>
      </CardGrid>

      </section>

      <section className={s.block} data-demo="filter">
      <div className={s.heading}>Filterzeile</div>
      <FilterBar>
        <FilterGrow>
          <Input label="Suche" placeholder="Bezeichnung oder Kennung"
                 value={search}
                 onChange={(event) => { setSearch(event.target.value); setPage(1) }} />
        </FilterGrow>
        <FilterFixed>
          <Select label="Zustand" value={group}
                  onChange={(event) => { setGroup(event.target.value); setPage(1) }}>
            <option value="alle">alle</option>
            <option value="gut">gut</option>
            <option value="mittel">mittel</option>
            <option value="schlecht">schlecht</option>
          </Select>
        </FilterFixed>
        <Toggle on={onlyWithNote}
                onChange={(on) => { setOnlyWithNote(on); setPage(1) }}>
          nur mit Bemerkung
        </Toggle>
        <FilterEnd>
          <FilterCount>{filtered.length} von {ROWS.length}</FilterCount>
          <Button onClick={() => {
            setSearch(''); setGroup('alle'); setOnlyWithNote(false); setPage(1)
          }}>Zurücksetzen</Button>
        </FilterEnd>
      </FilterBar>

      </section>

      <section className={s.block} data-demo="mehrfachauswahl">
      <div className={s.heading}>
        Mehrfachauswahl mit Suche — findet über Kürzel UND Kennung
      </div>
      <FilterBar>
        <FilterFixed>
          <MultiSelect label="Handelsplätze" options={PLAETZE}
                       selected={plaetze} onChange={setPlaetze}
                       placeholder="alle Plätze"
                       searchPlaceholder="XNAS, NASDAQ oder New York …"
                       help="Die Suche greift auf Kürzel, Kennung und den vollen Namen zu. Wer den MIC kennt, tippt XNAS; wer das Kürzel kennt, NASDAQ — beides führt zum selben Eintrag." />
        </FilterFixed>
        <FilterEnd>
          <FilterCount>
            {plaetze.length ? `${plaetze.length} gewählt` : 'nichts gewählt'}
          </FilterCount>
          <Button onClick={() => setPlaetze([])} disabled={!plaetze.length}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>
      <p className={s.note}>
        Tastatur: <strong>↓</strong> öffnet, <strong>↑↓</strong> blättert,
        {' '}<strong>Enter</strong> schaltet um, <strong>Esc</strong> schließt.
        Der Fokus bleibt dabei im Suchfeld — tippen und auswählen ohne
        Handwechsel. Gewählte Einträge bleiben stehen, auch wenn die Suche sie
        gerade ausblendet.
      </p>
      </section>

      <section className={s.block} data-demo="tabelle-fest">
      <div className={s.heading}>Tabelle mit fester Höhe — Scrollen im Körper</div>
      <Card flush span={12}>
        <DataTable
          columns={columns}
          rows={ROWS}
          rowKey={(row) => row.id}
          // KEIN `page`: Alle 47 Zeilen sind geladen. Die Auswahl unten
          // bestimmt nur, wie viele davon ohne Scrollen im Bild stehen.
          status={`${ROWS.length} Zeilen geladen`}
          visibleRows={sichtbar}
          onVisibleRows={setSichtbar}
        />
      </Card>
      <Note>
        Die Tabelle bekommt eine feste Höhe und scrollt in sich selbst — die
        Seite darunter bleibt, wo sie ist. Die Kopfzeile haftet dabei oben,
        sonst wüsste man in der Mitte von fünfzig Zeilen nicht mehr, welche
        Spalte welche ist. Die erste Spalte haftet weiterhin links; wo beide
        sich kreuzen, liegt der Kopf oben.
      </Note>
      <Note>
        Die Höhe wird an den TATSÄCHLICH gerenderten Zeilen gemessen, nicht
        aus einer Zeilenhöhe gerechnet: Sobald eine Spalte umbricht — und
        mehrere tun das — wäre jede Formel falsch.
      </Note>
      <Note>
        „alle“ ist der Ausgangszustand: Dann wächst die Tabelle wie jede
        andere mit ihrem Inhalt, und die Seite scrollt. Ohne
        `onVisibleRows` erscheint das Auswahlfeld gar nicht erst — eine
        Tabelle, deren Höhe die Ansicht vorgibt, soll niemand verstellen.
      </Note>
      </section>

      <section className={s.block} data-demo="tabelle">
      <div className={s.heading}>Tabelle — sortierbar, blätterbar, seitlich scrollbar</div>
      <Card flush span={12}>
        <DataTable
          columns={columns}
          rows={visible}
          rowKey={(row) => row.id}
          sort={sort}
          onSort={(next) => { setSort(next); setPage(1) }}
          page={{ page, size: PAGE_SIZE, total: filtered.length }}
          onPage={setPage}
          onRowClick={setSelected}
          empty={<EmptyState title="keine Treffer">
            Filter zurücksetzen oder Suchbegriff kürzen.
          </EmptyState>}
        />
      </Card>
      <Note>
        Fenster schmaler ziehen: Ab 1280 px verschwindet die Bemerkung, unter
        768 px auch das Datum. Der Rest scrollt seitlich, die Bezeichnung bleibt
        stehen. Rechts zeigt eine Schattenkante, dass es weitergeht.
      </Note>
      <Note>
        Über eine Zeile fahren: Kennung und Betrag zeigen einen Kopierknopf. Bei
        der Kennung sitzt er am rechten Zellenrand, beim Betrag vor der Zahl —
        dort müssen die Ziffern rechts untereinander bleiben. Nach dem Klick
        wird das Zeichen kurz zu einem grünen Häkchen.
      </Note>

      </section>

      <section className={s.block} data-demo="tabelle-ohne-total">
      <div className={s.heading}>Tabelle — ohne bekannte Gesamtzahl</div>
      <Card flush span={12}>
        <DataTable
          columns={columns.slice(0, 4)}
          rows={ROWS.slice(0, 5)}
          rowKey={(row) => row.id}
          page={{ page: 3, size: 5, total: null, hasMore: true }}
          onPage={() => {}}
        />
      </Card>
      <Note>
        `total: null` heißt „nicht gezählt", nicht „null Treffer" — die
        Statuszeile nennt dann nur den Bereich, und geblättert wird über Vor und
        Zurück.
      </Note>

      </section>

      <section className={s.block} data-demo="detail">
      <div className={s.heading}>Detailseite — beschriftete Werte</div>
      <CardGrid>
        <DetailSection title="Zwei Spalten" span={6} columns={2}
                       meta="Stand 18.08.2026">
          {/* Kopierknopf am Feld — im Gegensatz zur Tabelle immer sichtbar. */}
          <Field label="Kennung" value={selected?.code ?? 'XX0000000001'} mono
                 copy={selected?.code ?? 'XX0000000001'} />
          <Field label="Bezeichnung"
                 value={selected?.name ?? 'Auf eine Tabellenzeile klicken'} />
          <Field label="Betrag" value={money.format(selected?.amount ?? 1000)}
                 mono emphasis
                 copy={(selected?.amount ?? 1000).toFixed(2)}
                 help="Hervorgehoben: der eine Wert, um den es auf der Seite geht. Der Knopf kopiert 1000.00, nicht 1.000,00." />
          <Field label="Anteil" value={percent.format(selected?.share ?? 0)} mono />
          <FieldDivider />
          <Field label="Fehlender Wert" value={selected?.note} />
          <Field label="Zustand" value={
            <Chip tone={tone(selected?.state ?? 'gut')}>
              {selected?.state ?? 'gut'}
            </Chip>
          } />
          <Field label="Über die ganze Zeile" wide
                 value="Ein längerer Text, der über beide Spalten läuft. Für Freitext und Aufzählungen, die in einer halben Zeile nicht lesbar wären." />
        </DetailSection>

        <DetailSection title="Drei Spalten" span={6} columns={3}
                       help="Auf dem Desktop drei, auf dem Tablet zwei, am Handy eine — drei Spalten auf 375 px wären 100 px je Wert.">
          <Field label="Erster" value="Wert" />
          <Field label="Zweiter" value="Wert" />
          <Field label="Dritter" value="Wert" />
          <Field label="Sehr lange Beschriftung, die umbrechen muss"
                 value="Wert" />
          <Field label="Ohne Wert" />
          <Field label="Monospace" value="0000-1111-2222" mono />
        </DetailSection>
      </CardGrid>

      </section>

      <section className={s.block} data-demo="knoepfe">
      <div className={s.heading}>Knöpfe</div>
      <Card span={12}>
        <div className={s.row}>
          <Button variant="primary">Primär</Button>
          <Button>Normal</Button>
          <Button variant="quiet">Zurückgenommen</Button>
          <Button variant="danger">Gefährlich</Button>
          <Button disabled>Gesperrt</Button>
        </div>
      </Card>

      </section>

      <section className={s.block} data-demo="pillen">
      <div className={s.heading}>Pillen</div>
      <Card span={12}>
        <div className={s.row}>
          <Chip>neutral</Chip>
          <Chip tone="info">info</Chip>
          <Chip tone="good">gut</Chip>
          <Chip tone="warn">Achtung</Chip>
          <Chip tone="bad">schlecht</Chip>
          <Chip tone="outline">nur Rahmen</Chip>
        </div>
      </Card>

      </section>

      <section className={s.block} data-demo="eingaben">
      <div className={s.heading}>Eingaben</div>
      <CardGrid>
        <Card span={6}>
          <div className={s.stack}>
            <Input label="Einfach" placeholder="mit Platzhalter" />
            <Input label="Mit Hinweis" hint="Erklärung unter dem Feld" />
            <Input label="Mit Fehler" error="So sieht ein Fehler aus"
                   defaultValue="falsch" />
          </div>
        </Card>
        <Card span={6}>
          <div className={s.stack}>
            <Select label="Auswahl" help="Der Pfeil ist selbst gezeichnet — der des Systems sieht auf jeder Plattform anders aus.">
              <option>erste Möglichkeit</option>
              <option>zweite Möglichkeit</option>
            </Select>
            <Input label="Gesperrt" disabled defaultValue="nicht änderbar" />
            <div className={s.row}>
              <Toggle on={onlyWithNote} onChange={setOnlyWithNote}>Schalter</Toggle>
              <span>
                Text mit Erklärung
                <HelpIcon term="Fragezeichen">
                  Öffnet auf Überfahren und auf Antippen. Nur Überfahren wäre am
                  Handy nichts wert — dort gibt es kein Überfahren.
                </HelpIcon>
              </span>
            </div>
          </div>
        </Card>
      </CardGrid>

      </section>

      <section className={s.block} data-demo="zustaende">
      <div className={s.heading}>Zustände</div>
      <CardGrid>
        <Card title="lädt" label span={4}><Loading /></Card>
        <Card title="leer" label span={4}>
          <EmptyState title="nichts gefunden">
            Mit einem Hinweis, was zu tun wäre.
          </EmptyState>
        </Card>
        <Card title="Meldungen" label span={4}>
          <div className={s.section}>
            <ErrorBox>Etwas ist schiefgegangen.</ErrorBox>
            <WarnBox>Etwas ist einzuschränken, aber nicht kaputt.</WarnBox>
            <Note>Eine Nebenbemerkung.</Note>
          </div>
        </Card>
      </CardGrid>

      </section>

      <section className={s.block} data-demo="breiten">
      <div className={s.heading}>Breite des Inhalts</div>
      <Card span={12} title="full — die ganze Breite" label>
        <span className={s.spanMark}>
          Der Standard. Fuer Listen, Tabellen und Dashboards: Jede Spalte, die
          es gibt, soll auch Platz haben.
        </span>
      </Card>

      <ContentWidth size="medium">
        <Card span={12} title="medium — 1040 px, mittig" label>
          <span className={s.spanMark}>
            Fuer Detailansichten mit zwei Spalten. Links und rechts bleibt Rand.
          </span>
        </Card>
        <DetailSection title="So sieht ein Abschnitt darin aus" span={12}
                       columns={2} meta="medium">
          <Field label="Kennung" value="XX0000000001" mono />
          <Field label="Bezeichnung" value="Ein Eintrag" />
          <Field label="Betrag" value="1.000,00" mono emphasis />
          <Field label="Anteil" value="52 %" mono />
        </DetailSection>
      </ContentWidth>

      <ContentWidth size="narrow">
        <Card span={12} title="narrow — 720 px, mittig" label>
          <span className={s.spanMark}>
            Fuer einspaltige Ansichten, die wie ein Formular gelesen werden.
          </span>
        </Card>
        <DetailSection title="Einspaltig" span={12} columns={2} meta="narrow">
          <Field label="Kennung" value="XX0000000002" mono />
          <Field label="Bezeichnung" value="Noch ein Eintrag" />
          <Field label="Ueber die ganze Zeile" wide
                 value="Bei dieser Breite laeuft das Auge nicht mehr zwischen Beschriftung und Wert quer ueber den Schirm." />
        </DetailSection>
      </ContentWidth>
      </section>

      <section className={s.block} data-demo="reiter-baum">
      <div className={s.heading}>Reiter und Baum</div>
      <CardGrid>
        <Card title="Reiter" label span={6}
              meta="Pfeiltasten wechseln, Pos1 und Ende springen"
              help="Umschalter zwischen Sichten auf denselben Inhalt. Der aktive trägt einen Balken darunter statt eines Rahmens — das ist ruhiger und passt zum Strich, den die Karte selbst schon benutzt. Nur der aktive Reiter ist mit Tab erreichbar; zwischen ihnen bewegt man sich mit den Pfeiltasten.">
          <Tabs items={REITER} active={reiter} onChange={setReiter}
                aria-label="Vorführung" />
          <p className={s.breakpoints} style={{ marginTop: 12 }}>
            gewählt: <b>{reiter}</b>
          </p>
        </Card>

        <Card title="Baum" label span={6}
              meta="Enter wählt · Pfeil rechts auf, links zu"
              help="Auf- und zuklappbar, kontrolliert wie die Mehrfachauswahl: Welche Knoten offen sind, weiß der Aufrufer — nur so lässt sich der Zustand in die Adresse schreiben. Ein Knoten ohne Kinder bekommt kein Dreieck, aber dieselbe Einrückung wie einer mit; sonst springt die Beschriftungsspalte.">
          <Tree nodes={BAUM} expanded={offen} selected={gewaehlt}
                columns={{ label: 'Branche', hint: 'Code', value: 'Titel' }}
                onToggle={(id) => setOffen((jetzt) => jetzt.includes(id)
                  ? jetzt.filter((k) => k !== id) : [...jetzt, id])}
                onSelect={(node) => setGewaehlt(node.id)}
                aria-label="Vorführung" />
        </Card>
      </CardGrid>
      </section>

      <section className={s.block} data-demo="verweis-popover">
      <div className={s.heading}>Verweis mit Popover</div>
      <Card span={12}>
        <div className={s.section}>
          <div className={s.hint}>
            Der Klick auf einen Verweis öffnet das Popover und navigiert
            NICHT mehr. Auf die Stammdaten führt allein der Knopf darin.
            Schließt per ✕, Klick daneben oder Esc.
          </div>
          <div className={s.refZeile}>
            {REF_DEMOS.map((detail, i) => (
              <RefPopover key={detail.ref} detail={detail}
                          className={detail.name === null
                            ? s.refOffen : s.refZiel}>
                {REF_TEXTE[i]}
              </RefPopover>
            ))}
          </div>
        </div>
      </Card>
      </section>

      <section className={s.block} data-demo="stufen">
      <div className={s.heading}>Stufen</div>
      <Card span={12}>
        <span className={s.breakpoints}>
          Mobile <b>&lt; 768</b> · Tablet <b>768 – 1279</b> ·
          Desktop <b>&ge; 1280</b>
        </span>
      </Card>
      </section>
    </>
  )
}
